import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type IProductRequestRepository from '@repositories/productRequestRepository/IProductRequestRepository';
import type {
  ProductRequestVendorFilter,
  ProductRequestVendorListDTO,
} from '@repositories/productRequestRepository/productRequest.types';
import type { Page } from '@type/Page';
import { cleanString } from '@utils/CommonUtil';

const productRequestRepo = repository.get<IProductRequestRepository>(KEYS.PRODUCT_REQUEST_REPOSITORY);

const productRequestQueryKey = {
  all: ['productRequest'] as const,
  lists: () => [...productRequestQueryKey.all, 'list'] as const,
  vendorList: (queries?: ProductRequestVendorFilter) => [productRequestQueryKey.lists(), { ...queries }] as const,
};

export const useAddProductRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: productRequestRepo.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: productRequestQueryKey.lists() });
    },
  });
};

export const useProductRequestVendor = (
  queries?: ProductRequestVendorFilter,
  options?: UseInfiniteQueryResult<Page<ProductRequestVendorListDTO>>,
) => {
  const result = useInfiniteQuery({
    queryKey: productRequestQueryKey.vendorList(queries),
    queryFn: async ({ pageParam = 0 }) => {
      const result = await productRequestRepo.getProductRequestVendor({ ...queries, page: pageParam });
      return {
        ...result,
        content: result.content
          .flat()
          .map(item => cleanString(item))
          .filter(item => item !== ''),
      };
    },
    ...options,
    getNextPageParam: lastPage => (lastPage.last ? undefined : lastPage.number + 1),
  });
  return { ...result, data: result.data };
};
